import type { FC } from 'react';
import { useState, useEffect, useCallback, useRef } from 'react';
import './Calculator.css';
import { CalculatorIcon, X } from 'lucide-react';
import TexasLogo from '../../Assests/texas_logo.png';
interface CalculatorProps {
  isOpen: boolean;
  onClose: () => void;
}

export const Calculator: FC<CalculatorProps> = ({ isOpen, onClose }) => {
  const [display, setDisplay] = useState("0");
  const [previousValue, setPreviousValue] = useState<number | null>(null);
  const [pendingOperator, setPendingOperator] = useState<string | null>(null);
  const [waitingForOperand, setWaitingForOperand] = useState(false);
  const [memory, setMemory] = useState<number>(0); // Changed to 0 instead of null for simpler logic
  const [hasMemory, setHasMemory] = useState(false);
  const [memoryRecalled, setMemoryRecalled] = useState(false);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [position, setPosition] = useState({ top: 80, left: window.innerWidth - 280 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const modalRef = useRef<HTMLDivElement>(null);

  const triggerFlash = useCallback((key: string) => {
    setActiveKey(key);
    setTimeout(() => setActiveKey(null), 120);
  }, []);

  const clearAll = useCallback(() => {
    triggerFlash('clear');
    setDisplay("0");
    setPreviousValue(null);
    setPendingOperator(null);
    setWaitingForOperand(false);
    setMemoryRecalled(false);
  }, [triggerFlash]);

  const toggleSign = useCallback(() => {
    triggerFlash('pm');
    setMemoryRecalled(false);
    if (display === "Error" || display === "0") return;
    setDisplay(prev => prev.startsWith('-') ? prev.slice(1) : '-' + prev);
  }, [display, triggerFlash]);

  const inputDigit = useCallback((digit: string) => {
    triggerFlash(digit);
    setMemoryRecalled(false);

    if (waitingForOperand) {
      setDisplay(digit);
      setWaitingForOperand(false);
    } else {
      setDisplay(prev => {
        if (prev === "Error" || prev === "0") return digit;
        // Limit to 8 digits
        const digitCount = prev.replace(/[^0-9]/g, '').length;
        if (digitCount >= 8) return prev;
        return prev + digit;
      });
    }
  }, [waitingForOperand, triggerFlash]);

  const inputDecimal = useCallback(() => {
    triggerFlash('.');
    setMemoryRecalled(false);

    if (waitingForOperand) {
      setDisplay("0.");
      setWaitingForOperand(false);
    } else {
      setDisplay(prev => {
        if (prev === "Error") return "0.";
        if (!prev.includes('.')) return prev + '.';
        return prev;
      });
    }
  }, [waitingForOperand, triggerFlash]);

  const calculate = useCallback((nextOperator: string) => {
    triggerFlash(nextOperator);
    setMemoryRecalled(false);

    const current = parseFloat(display);
    if (isNaN(current)) return;

    if (previousValue === null) {
      setPreviousValue(current);
    } else if (pendingOperator && !waitingForOperand) {
      let result = previousValue;
      switch (pendingOperator) {
        case '+': result += current; break;
        case '-': result -= current; break;
        case '*': result *= current; break;
        case '/':
          if (current === 0) {
            setDisplay("Error");
            setPreviousValue(null);
            setPendingOperator(null);
            return;
          }
          result /= current;
          break;
      }
      setPreviousValue(result);
      const formattedResult = result.toString();
      if (formattedResult.replace(/[^0-9]/g, '').length > 8) {
        if (Math.abs(result) >= 100000000) {
          setDisplay("Error");
          setPreviousValue(null);
          setPendingOperator(null);
        } else {
          setDisplay(result.toPrecision(8).replace(/\.?0+$/, ''));
        }
      } else {
        setDisplay(formattedResult);
      }
    }

    setPendingOperator(nextOperator);
    setWaitingForOperand(true);
  }, [display, previousValue, pendingOperator, waitingForOperand, triggerFlash]);

  const equals = useCallback(() => {
    triggerFlash('=');
    setMemoryRecalled(false);

    if (!pendingOperator || previousValue === null) return;

    const current = parseFloat(display);
    if (isNaN(current)) return;

    let result = previousValue;
    switch (pendingOperator) {
      case '+': result += current; break;
      case '-': result -= current; break;
      case '*': result *= current; break;
      case '/':
        if (current === 0) {
          setDisplay("Error");
          return;
        }
        result /= current;
        break;
    }

    const formattedResult = result.toString();
    if (formattedResult.replace(/[^0-9]/g, '').length > 8) {
      if (Math.abs(result) >= 100000000) {
        setDisplay("Error");
        setPreviousValue(null);
        setPendingOperator(null);
        return;
      } else {
        // Round or truncate to 8 digits
        setDisplay(result.toPrecision(8).replace(/\.?0+$/, ''));
      }
    } else {
      setDisplay(formattedResult);
    }
    setPreviousValue(null);
    setPendingOperator(null);
    setWaitingForOperand(true);
  }, [display, pendingOperator, previousValue, triggerFlash]);

  const squareRoot = useCallback(() => {
    triggerFlash('sqrt');
    setMemoryRecalled(false);
    const val = parseFloat(display);
    if (isNaN(val) || val < 0) {
      setDisplay("Error");
      return;
    }
    const result = Math.sqrt(val);
    const formattedResult = result.toString();
    if (formattedResult.replace(/[^0-9]/g, '').length > 8) {
      setDisplay(result.toPrecision(8).replace(/\.?0+$/, ''));
    } else {
      setDisplay(formattedResult);
    }
    setWaitingForOperand(true);
  }, [display, triggerFlash]);

  const percent = useCallback(() => {
    triggerFlash('percent');
    setMemoryRecalled(false);
    const val = parseFloat(display);
    if (isNaN(val)) return;
    const result = val / 100;
    const formattedResult = result.toString();
    if (formattedResult.replace(/[^0-9]/g, '').length > 8) {
      setDisplay(result.toPrecision(8).replace(/\.?0+$/, ''));
    } else {
      setDisplay(formattedResult);
    }
    setWaitingForOperand(true);
  }, [display, triggerFlash]);

  const memoryAdd = useCallback(() => {
    triggerFlash('m-plus');
    setMemoryRecalled(false);
    const val = parseFloat(display);
    if (isNaN(val)) return;
    setMemory(prev => prev + val);
    setHasMemory(true);
    setWaitingForOperand(true);
  }, [display, triggerFlash]);

  const memorySubtract = useCallback(() => {
    triggerFlash('m-minus');
    setMemoryRecalled(false);
    const val = parseFloat(display);
    if (isNaN(val)) return;
    setMemory(prev => prev - val);
    setHasMemory(true);
    setWaitingForOperand(true);
  }, [display, triggerFlash]);

  const handleMRC = useCallback(() => {
    triggerFlash('mrc');
    if (!hasMemory) return;

    if (memoryRecalled) {
      setMemory(0);
      setHasMemory(false);
      setMemoryRecalled(false);
    } else {
      const formattedMemory = memory.toString();
      if (formattedMemory.replace(/[^0-9]/g, '').length > 8) {
        setDisplay(memory.toPrecision(8).replace(/\.?0+$/, ''));
      } else {
        setDisplay(formattedMemory);
      }
      setWaitingForOperand(true);
      setMemoryRecalled(true);
    }
  }, [hasMemory, memory, memoryRecalled, triggerFlash]);

  // Keyboard handling
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Prevent default only for keys we handle
      if ('0123456789.+*/=-'.includes(e.key) || ['Enter', 'Backspace', 'Escape', 'p', 'm', 'c'].includes(e.key.toLowerCase())) {
        e.preventDefault();
      }

      const key = e.key.toLowerCase();

      if (key >= '0' && key <= '9') inputDigit(key);
      else if (key === '.') inputDecimal();
      else if (key === '+') calculate('+');
      else if (key === '-') calculate('-');
      else if (key === '*' || key === 'x') calculate('*');
      else if (key === '/') calculate('/');
      else if (key === 'enter' || key === '=') equals();
      else if (key === 'backspace') clearAll();
      else if (key === 'escape') onClose();
      else if (key === 'p') memoryAdd();
      else if (key === 'm') memorySubtract();
      else if (key === 'c') handleMRC();
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    isOpen, onClose, inputDigit, inputDecimal, calculate, equals, clearAll,
    memoryAdd, memorySubtract, handleMRC
  ]);

  // Dragging
  const startDrag = (e: React.MouseEvent) => {
    if (modalRef.current) {
      const rect = modalRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      setIsDragging(true);
      e.preventDefault();
    }
  };

  useEffect(() => {
    const move = (e: MouseEvent) => {
      if (!isDragging || !modalRef.current) return;
      let newX = e.clientX - dragOffset.x;
      let newY = e.clientY - dragOffset.y;

      // Keep inside viewport
      const w = modalRef.current.offsetWidth;
      const h = modalRef.current.offsetHeight;
      newX = Math.max(0, Math.min(window.innerWidth - w, newX));
      newY = Math.max(0, Math.min(window.innerHeight - h, newY));

      setPosition({ left: newX, top: newY });
    };

    const stop = () => setIsDragging(false);

    if (isDragging) {
      window.addEventListener('mousemove', move);
      window.addEventListener('mouseup', stop);
      return () => {
        window.removeEventListener('mousemove', move);
        window.removeEventListener('mouseup', stop);
      };
    }
  }, [isDragging, dragOffset]);

  if (!isOpen) return null;

  return (
    <div className="calculator-overlay">
      <div
        ref={modalRef}
        className="calculator-modal"
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
          position: 'absolute'
        }}
      >
        <div
          className="calculator-header f-tahoma"
          onMouseDown={startDrag}
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '16px'}} className="f-tahoma">
            <CalculatorIcon size={18} />
            Calculator
          </span>
          <button onClick={onClose} className="calc-close-btn"><X strokeWidth={4} size={32} /></button>
        </div>

        <div className="calculator-body">
          <div className="calculator-display-container">
            <div className="calculator-display">
              {hasMemory && <span className="memory-indicator">M</span>}
              <span className="display-value">{display}</span>
            </div>
          </div>

          <div className="calculator-logo">
            <div className="ti-logo">
              <span className="ti-symbol">
                <img src={TexasLogo} alt="TI Logo" />
              </span>
              <span className="text-[9px] f-serif" style={{fontWeight: 400, letterSpacing: "0.5px", color: "#FFF"}}><span style={{fontSize: "12px"}} className="f-serif">T</span>EXAS <span style={{fontSize: "12px"}} className="f-serif">I</span>NSTRUMENTS</span>
            </div>
            <div className="text-[18px] font-[900] tracking-wide f-courier"
              style={{fontWeight: 900, fontSize: "28px", letterSpacing: "-2.3px", marginRight: "3px"}}>
              TI-108
            </div>

          </div>

          <div className="calculator-controls-outer">
            <div className="calculator-keypad f-courier">
              <button className={`calc-btn btn-size red f-courier ${activeKey === 'pm' ? 'active' : ''}`} onClick={toggleSign}>+/−</button>
              <button className={`calc-btn btn-size f-courier red ${activeKey === 'sqrt' ? 'active' : ''}`} onClick={squareRoot}>√</button>
              <button className={`calc-btn btn-size f-courier red ${activeKey === 'percent' ? 'active' : ''}`} onClick={percent}>%</button>
              <button className={`calc-btn btn-size f-courier red operator ${activeKey === '/' ? 'active' : ''}`} onClick={() => calculate("/")}>÷</button>

              <button className={`calc-btn btn-size f-courier red ${activeKey === 'mrc' ? 'active' : ''}`} onClick={handleMRC}>MRC</button>
              <button className={`calc-btn btn-size f-courier red ${activeKey === 'm-minus' ? 'active' : ''}`} onClick={memorySubtract}>M−</button>
              <button className={`calc-btn btn-size f-courier red ${activeKey === 'm-plus' ? 'active' : ''}`} onClick={memoryAdd}>M+</button>
              <button className={`calc-btn btn-size f-courier red operator ${activeKey === '*' ? 'active' : ''}`} onClick={() => calculate("*")}>×</button>

              <button className={`calc-btn btn-size f-courier ${activeKey === '7' ? 'active' : ''}`} onClick={() => inputDigit("7")}>7</button>
              <button className={`calc-btn btn-size f-courier ${activeKey === '8' ? 'active' : ''}`} onClick={() => inputDigit("8")}>8</button>
              <button className={`calc-btn btn-size f-courier ${activeKey === '9' ? 'active' : ''}`} onClick={() => inputDigit("9")}>9</button>
              <button className={`calc-btn btn-size f-courier red operator ${activeKey === '-' ? 'active' : ''}`} onClick={() => calculate("-")}>−</button>

              <button className={`calc-btn btn-size f-courier ${activeKey === '4' ? 'active' : ''}`} onClick={() => inputDigit("4")}>4</button>
              <button className={`calc-btn btn-size f-courier ${activeKey === '5' ? 'active' : ''}`} onClick={() => inputDigit("5")}>5</button>
              <button className={`calc-btn btn-size f-courier ${activeKey === '6' ? 'active' : ''}`} onClick={() => inputDigit("6")}>6</button>
              <button className={`calc-btn btn-size f-courier red operator ${activeKey === '+' ? 'active' : ''}`} onClick={() => calculate("+")}>+</button>

              <button className={`calc-btn btn-size f-courier ${activeKey === '1' ? 'active' : ''}`} onClick={() => inputDigit("1")}>1</button>
              <button className={`calc-btn btn-size f-courier ${activeKey === '2' ? 'active' : ''}`} onClick={() => inputDigit("2")}>2</button>
              <button className={`calc-btn btn-size f-courier ${activeKey === '3' ? 'active' : ''}`} onClick={() => inputDigit("3")}>3</button>
              <button className={`calc-btn btn-size-equal f-courier red operator equals vertical ${activeKey === '=' ? 'active' : ''}`} onClick={equals}>=</button>

              <button className={`calc-btn btn-size f-courier red clear ${activeKey === 'clear' ? 'active' : ''}`} style={{paddingLeft: "2px"}} onClick={clearAll}>ON/C</button>
              <button className={`calc-btn btn-size f-courier ${activeKey === '0' ? 'active' : ''}`} onClick={() => inputDigit("0")}>0</button>
              <button className={`calc-btn btn-size f-courier ${activeKey === '.' ? 'active' : ''}`} onClick={inputDecimal}>•</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};